// Rankings — a leaderboard that ranks the whole population for one metric in one
// season, ported from js/renderers-rankings.js via the 04b bridge.
//   04g-3a (CORE): the grouped/searchable metric select + season select, the
//     population-readout (count + Per 90/Total toggle), the ranked list, drag-source rows.
//   04g-3b (this): the deferred .filter-bar row — the compare-against filter CHIPS (the
//     legacy StatsFilterKit chip-bar, wired imperatively — the HYBRID) + the
//     find-a-player search (React, coupled to the React list's highlight/scroll).
//
// needsPlayer:false — a Ranking isn't about one player. It's created by dragging a Stats
// row's table icon (which seeds _rankingCategoryId + _rankingMetricKey) or by a "… Rank"
// side-panel tile (seeds only the category → first metric defaulted); a blank module shows
// the empty state. Instance state (mod._ranking*) is captured by main.js saveState/undo/presets.
//
// The filter-bar: the compare-against chips are the shared React <FilterChipBar> (04h-1 —
// mod._rankingFilters mutated in place; onChange bumps a version counter → a FRESH clone into
// useRanking so it recomputes). The find-a-player search is React too, coupled to the React
// list (row highlight + scroll); its outside-click close is our own effect. (Before 04h-1 the
// chips were the legacy window.StatsFilterKit imperative island — now retired.)
//
// ?live=1 (Track 2 demo push): the board (`useRankingBoard`) ranks her league's Stats file
// instead of the snapshot — the Ranking's player's league, its built seasons in the picker, the
// chips the files have data for. The widget's look is unchanged.

import { ModuleExplainer, shortNameOrPlayer } from "./ModuleExplainer.js";
import { explainRanking } from "./explain.js";
import { searchLivePlayers, MIN_SEARCH_LETTERS } from "../live/player-search.js";
import { rememberPickedPlayer, type PickedPlayer } from "../live/picked-players.js";
import { loadStickyClub } from "../live/squad.js";
import { liveApi } from "../live/api.js";
import { isLeagueCleared, isLeagueEmpty, useLeagueChip } from "../hooks/useLeagueChip.js";
import { useGenderSwitch } from "../hooks/useGenderSwitch.js";
import { sideOf, womensOf } from "../live/gender-switch.js";
import { GenderSwitch } from "./GenderSwitch.js";
import { positionRowKey, whyTwice, type PositionRow } from "../live/group-players.js";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import type { ProtoRankingEntry } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { SubControls } from "./SubControls.js";
import { StyledSelect, type StyledSelectGroup, type StyledSelectItem } from "./StyledSelect.js";
import { PopulationReadout } from "./PopulationReadout.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { matchRankingSearch } from "./rankings-search.js";
import { FilterChipBar } from "./FilterChipBar.js";
import { snapshotPlayersOrNone } from "../data/snapshot.js";
import { useRankingMetric, useFirstRankingMetricKey, useRankingBoard } from "../hooks/useRankings.js";
import { LIVE } from "../live/flag.js";
import {
  hasTotalMode,
  formatValueForMode,
  isRangeFilter,
  formatRangeReadout,
  formatCategoricalValue,
  FILTER_LABELS,
  type ActiveFilters,
  type RangeActive,
  type CategoricalActive,
} from "../engines/stats-filter-kit.js";
import { getStickyPlayer, setStickyPlayer } from "../canvas/sticky-player.js";
import { playerById, playerIdOf, type PlayerId } from "../data/players.js";
import { startPlayerTokenDrag } from "../canvas/menu-drag.js";
import { noteRowPlayer } from "../live/player-gender.js";

export function RankingsModule({ mod, body }: ProtoModuleProps) {
  const categoryId = getModState<string>(mod, "_rankingCategoryId") ?? null;
  const firstMetricKey = useFirstRankingMetricKey(categoryId);
  const [metricKey, setMetricKey] = useState<string | null>(
    () => getModState<string>(mod, "_rankingMetricKey") ?? firstMetricKey,
  );

  // The season picked; null = the default (the board resolves it: the snapshot's newest, or live
  // her current season). Saved to the `_`-state at once (T2-1 pattern: Retry retries the pick).
  const [seasonPick, setSeasonPick] = useState<string | null>(() => getModState<string>(mod, "_rankingSeason") ?? null);
  const [valueMode, setValueMode] = useState<string>(() =>
    getModState<string>(mod, "_rankingValueMode") === "total" ? "total" : "per90",
  );

  // The reference player is a purely-internal seed (used only for a categorical filter
  // chip's default; never surfaced). Seed once from the sticky default, exactly like the
  // legacy (renderers-rankings.js:219).
  // Stored by id (ids everywhere).
  const [sourcePlayer] = useState<PlayerId | null>(() => {
    const existing = getModState<PlayerId>(mod, "_rankingSourcePlayer");
    if (existing) return existing;
    const players = snapshotPlayersOrNone();
    const sticky = getStickyPlayer();
    const first = Object.values(players)[0];
    // Live: any player in the DB (her league is the group). Snapshot: one it has.
    const known = sticky !== null && (LIVE || playerById(players, sticky) !== undefined);
    const resolved = known ? sticky : first ? playerIdOf(first) : null;
    setModState(mod, "_rankingSourcePlayer", resolved ?? undefined);
    return resolved;
  });

  // The module's own compare-against filter state — the SAME mutable object the legacy
  // chip-bar mutates in place (mod._rankingFilters). Seeded once; a stable reference so
  // renderFilterBar's in-place edits accumulate across re-renders.
  const [activeFilters] = useState<ActiveFilters>(() => {
    const existing = getModState<ActiveFilters>(mod, "_rankingFilters");
    if (existing) return existing;
    const fresh: ActiveFilters = {};
    setModState(mod, "_rankingFilters", fresh);
    return fresh;
  });
  // Bumped by the chip-bar's `rerender` (and any filter mutation) to force a recompute.
  const [filterVersion, setFilterVersion] = useState(0);
  // A fresh clone per version — useRanking depends on `active` by reference, so a new
  // object is how a chip change reaches the memoised ranking.
  const activeSnapshot = useMemo<ActiveFilters>(
    () => ({ ...activeFilters }),
    [activeFilters, filterVersion],
  );

  const metricResult = useRankingMetric(categoryId, metricKey);
  const canToggleTotal = metricResult ? hasTotalMode(metricResult.metric) : false;
  const useTotal = canToggleTotal && valueMode === "total";

  // Live: the Men's / Women's switch (Ben, 2026-11-02): a flip lands on the other gender's top
  // league, its latest season.
  const bumpFilters = useCallback(() => setFilterVersion((v) => v + 1), []);
  const resetSeason = useCallback(() => {
    setSeasonPick(null);
    setModState(mod, "_rankingSeason", undefined);
  }, [mod]);
  const gender = useGenderSwitch(mod, "_rankingGender", "_rankingLeagueQuiet", activeFilters, bumpFilters, resetSeason);

  // Live: the user removed the League chip (✕) — empty until a league is picked or a player found.
  const leagueCleared = isLeagueCleared(mod, activeFilters, "_rankingLeagueQuiet");
  const board = useRankingBoard({
    leagueCleared,
    sourcePlayer,
    metric: metricResult?.metric ?? null,
    season: seasonPick,
    useTotal,
    active: activeSnapshot,
    womensPick: gender.pick ? womensOf(gender.pick) : null,
  });
  const { entries, season, seasonCodes, chips } = board;
  // Live: a player listed once per position says why on hover (Q-014). Empty on the snapshot site.
  const twice = useMemo(() => whyTwice(entries as PositionRow[]), [entries]);

  // Find-a-player search + jump (scoped to THIS rendered leaderboard). The jump is
  // remembered by player id (survives reorders / drops out silently) as _rankingJumpedPlayer.
  const [jumpedId, setJumpedId] = useState<PlayerId | null>(() => getModState<PlayerId>(mod, "_rankingJumpedPlayer") ?? null);
  const [query, setQuery] = useState("");
  const [resultsOpen, setResultsOpen] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Live: the League chip (useLeagueChip — quiet default = the canvas player's league).
  const { onChipsChange, quietKeys } = useLeagueChip(
    mod,
    activeFilters,
    board.defaultLeague,
    filterVersion,
    bumpFilters,
    "_rankingLeagueQuiet",
  );

  // Write the resolved selections back as `_`-state (saveState/undo/presets capture it).
  useEffect(() => {
    if (metricKey) setModState(mod, "_rankingMetricKey", metricKey);
  }, [mod, metricKey]);
  useEffect(() => {
    if (season) setModState(mod, "_rankingSeason", season);
  }, [mod, season]);
  useEffect(() => {
    setModState(mod, "_rankingValueMode", valueMode);
  }, [mod, valueMode]);

  useEffect(() => {
    body.style.justifyContent = metricResult ? "flex-start" : "";
  }, [body, metricResult]);

  // The heading reads "{Category} Ranking": the category name goes in a sibling
  // .module-title-prefix before .module-title (never mutate .module-title itself — it's
  // refreshModuleBody's lookup key). renderers-rankings.js:239.
  const categoryName = metricResult?.category.name ?? null;
  useEffect(() => {
    const titleEl = mod.querySelector(".module-title");
    if (!titleEl || !categoryName) return;
    let prefix = mod.querySelector<HTMLElement>(".module-title-prefix");
    if (!prefix) {
      prefix = document.createElement("div");
      prefix.className = "module-title-prefix";
      titleEl.parentNode?.insertBefore(prefix, titleEl);
    }
    prefix.textContent = categoryName;
    return () => prefix?.remove();
  }, [mod, categoryName]);

  // Restore the search box to a jumped player's name on mount/undo (the row carries the
  // short name once the list is built); only seeds an empty box, never overwrites typing.
  useEffect(() => {
    if (jumpedId === null) return;
    const e = entries.find((x) => x.playerId === jumpedId);
    if (e) setQuery((q) => (q ? q : e.shortName));
  }, [jumpedId, entries]);

  // Close the search results on an outside click (our own — the legacy delegated listener
  // targets the same class and would wipe React's DOM). mousedown so an option's click still lands.
  useEffect(() => {
    if (!resultsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!searchWrapRef.current?.contains(e.target as Node)) setResultsOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [resultsOpen]);

  // Re-locate + scroll to the jumped row after any reorder (metric/season/mode/filter) —
  // silently does nothing if that player no longer has a row here. renderers-rankings.js:570.
  useLayoutEffect(() => {
    if (jumpedId === null) return;
    const rowEl = listRef.current?.querySelector<HTMLElement>(".ranking-row-jumped");
    rowEl?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [jumpedId, entries]);

  const matches = useMemo(() => matchRankingSearch(entries, query), [entries, query]);

  // Live: "Find player…" also searches every player in the database (Ben, 2026-10-16), the same
  // search as the player boxes. Picking someone not in this list switches the League chip to
  // their league and jumps to them — e.g. from Kane's Bundesliga to Rice in two steps.
  const [hits, setHits] = useState<PickedPlayer[]>([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const q = query.trim();
    if (!LIVE || !resultsOpen || q.length < MIN_SEARCH_LETTERS) {
      setHits([]);
      setSearching(false);
      return;
    }
    let stale = false;
    setSearching(true);
    const t = window.setTimeout(() => {
      searchLivePlayers(liveApi(), q).then(
        (found) => {
          if (stale) return;
          setHits(found);
          setSearching(false);
        },
        (err: unknown) => {
          console.error("[live] the Ranking's player search failed:", err);
          if (stale) return;
          setHits([]);
          setSearching(false);
        },
      );
    }, 300);
    return () => {
      stale = true;
      window.clearTimeout(t);
    };
  }, [query, resultsOpen]);
  const elsewhere = useMemo(() => hits.filter((h) => !entries.some((e) => e.playerId === h.id)), [hits, entries]);
  const [findNote, setFindNote] = useState<string | null>(null);
  const jumpElsewhere = useCallback(
    async (p: PickedPlayer) => {
      rememberPickedPlayer(p);
      setResultsOpen(false);
      setQuery(p.name);
      setFindNote(null);
      const club = await loadStickyClub(liveApi(), p.id);
      const label = club.competitionId === null ? undefined : board.leagueOf?.(club.competitionId);
      if (!label) {
        setFindNote(`${p.name} has no current league with data`);
        return;
      }
      // Replaces the leagues shown (never adds: that could put a men's and a women's league together).
      // The switch follows the found player's gender (theirs is the league now shown).
      const theirs = board.womensOfLeague?.(label) ?? null;
      const ours = board.gender?.player ?? null;
      if (theirs !== null) setModState(mod, "_rankingGender", ours !== null && theirs === ours ? undefined : sideOf(theirs));
      activeFilters.league = label;
      setModState(mod, "_rankingLeagueQuiet", false);
      setModState(mod, "_rankingLeagueQuietCleared", undefined);
      setJumpedId(p.id);
      setModState(mod, "_rankingJumpedPlayer", p.id);
      setStickyPlayer(p.id);
      bumpFilters();
    },
    [mod, activeFilters, board, bumpFilters],
  );

  const jumpTo = useCallback(
    (id: PlayerId) => {
      setJumpedId(id);
      setModState(mod, "_rankingJumpedPlayer", id);
      const e = entries.find((x) => x.playerId === id);
      if (e) setQuery(e.shortName);
      // A found player becomes the sticky default — on ?live=1 noted first, so a module opened
      // next can open on her (she may not be in the snapshot).
      if (LIVE && e) noteRowPlayer({ id, name: e.name, team: e.team }, board.womens);
      setStickyPlayer(id);
      setResultsOpen(false);
    },
    [mod, entries, board.womens],
  );

  const clearJump = useCallback(() => {
    setJumpedId(null);
    setModState(mod, "_rankingJumpedPlayer", undefined);
    setQuery("");
    setResultsOpen(false);
    body.scrollTop = 0;
    searchInputRef.current?.focus();
  }, [mod, body]);

  const onSearchKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (matches[0]) jumpTo(matches[0].entry.playerId);
      else if (elsewhere[0]) void jumpElsewhere(elsewhere[0]);
    } else if (e.key === "Escape") {
      setResultsOpen(false);
      searchInputRef.current?.blur();
    }
  };

  // A row is a drag source (→ Bio on empty canvas, or joins a Radar/Stats), via
  // startPlayerTokenDrag, same as Squad's rows. On ?live=1 she's noted first (name + gender):
  // a live Stats tile takes her only once her gender is known.
  const onRowPointerDown = (e: ReactPointerEvent<HTMLDivElement>, entry: ProtoRankingEntry) => {
    if (window.innerWidth <= 768) return;
    e.preventDefault();
    e.stopPropagation();
    if (LIVE) noteRowPlayer({ id: entry.playerId, name: entry.name, team: entry.team }, board.womens);
    setStickyPlayer(entry.playerId);
    startPlayerTokenDrag(e.nativeEvent);
  };

  const metricItems = useMemo<StyledSelectGroup[]>(
    () =>
      metricResult
        ? metricResult.category.subcategories.map((sub) => ({
            group: sub.name,
            items: sub.playerMetrics.map((m) => ({ value: m.key, label: m.name })),
          }))
        : [],
    [metricResult],
  );
  const seasonItems = useMemo<StyledSelectItem[]>(
    () => seasonCodes.map((c) => ({ value: c, label: humanizeSeasonCode(c) })),
    [seasonCodes],
  );

  // Created via a drag/tile that seeds the category; a blank module has no sensible
  // default to rank (renderers-rankings.js:225).
  if (!metricResult) {
    return (
      <div className="stats-empty">
        No metric selected — drag the table icon from a Stats module row, or drop a “… Rank” tile
        from the side panel.
      </div>
    );
  }

  const { metric } = metricResult;
  const seasonLabel = humanizeSeasonCode(season ?? "");
  const showResults = resultsOpen && query.trim().length > 0;

  // Empty-state message: filtered-to-zero (name the active filters) vs a genuine data gap
  // (nobody recorded this metric this season). renderers-rankings.js:519.
  const activeKeys = Object.keys(activeSnapshot).filter((k) => activeSnapshot[k] !== undefined);
  const emptyMessage = activeKeys.length
    ? `No players match all of: ${activeKeys
        .map((k) =>
          isRangeFilter(k, chips.rangeFilters)
            ? `${chips.rangeFilters[k]!.chipLabel}: ${formatRangeReadout(k, activeSnapshot[k] as RangeActive, chips.rangeFilters)}`
            : `${FILTER_LABELS[k]}: ${formatCategoricalValue(activeSnapshot[k] as CategoricalActive)}`,
        )
        .join(", ")}. Remove a filter to widen the ranking.`
    : `No recorded ${useTotal ? "totals" : "values"} for ${metric.name} in ${seasonLabel}.`;

  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainRanking({
            metric: metric.name,
            mode: useTotal ? "total" : "per90",
            season: seasonLabel,
            filters: activeSnapshot,
            refName: shortNameOrPlayer(sourcePlayer),
            count: board.count,
            label: board.label ?? null,
            found: jumpedId === null ? null : (entries.find((e) => e.playerId === jumpedId)?.shortName ?? null),
            switchedTo: board.gender && board.gender.shown !== board.gender.player ? board.gender.shown : null,
          })}
        />
      )}
      <SubControls mod={mod} body={body}>
        <StyledSelect
          items={metricItems}
          value={metricKey ?? ""}
          onChange={(next) => {
            setMetricKey(next);
            setValueMode("per90"); // the new metric may have no Total equivalent
            body.scrollTop = 0;
          }}
          wrapClass="ranking-metric-select-wrap"
          extraClass="ranking-metric-select"
          searchable
        />
        <StyledSelect
          items={seasonItems}
          value={season ?? ""}
          onChange={(next) => {
            setSeasonPick(next);
            setModState(mod, "_rankingSeason", next);
            body.scrollTop = 0;
          }}
          wrapClass="ranking-season-select-wrap"
          extraClass="ranking-season-select"
        />
        {LIVE && board.gender && (
          <GenderSwitch womens={board.gender.shown} onFlip={() => gender.flip(board.gender!.shown, board.gender!.player)} />
        )}
      </SubControls>

      <PopulationReadout
        mod={mod}
        body={body}
        count={board.count}
        showToggle={canToggleTotal}
        mode={valueMode}
        onToggle={() => setValueMode((m) => (m === "total" ? "per90" : "total"))}
        label={board.label}
      />

      {/* Live with nothing to rank (a league's season isn't built yet): the readout says why,
          and the chips stay, so another league can be picked (Ben, 2026-10-16). */}
      {(board.count !== null || LIVE) && (
        <div className="ranking-wrap">
          {/* .filter-bar: the React search-wrap, then the shared React FilterChipBar
              (chips + "+ Add filter"). No more legacy chip-bar island. */}
          <div className="filter-bar">
            <div
              className="ranking-player-search-wrap"
              ref={searchWrapRef}
              style={board.count === null && !isLeagueEmpty(mod, activeFilters, "_rankingLeagueQuiet") ? { display: "none" } : undefined}
            >
              <input
                type="text"
                className="ranking-player-search"
                placeholder="Find player…"
                autoComplete="off"
                spellCheck={false}
                ref={searchInputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setResultsOpen(true);
                }}
                onKeyDown={onSearchKeyDown}
              />
              {jumpedId !== null && (
                <button type="button" className="ranking-search-clear" title="Clear" onClick={clearJump}>
                  ×
                </button>
              )}
              {showResults && (
                <div className="ranking-player-search-results open">
                  {matches.length ? (
                    matches.map((m) => (
                      <div
                        key={rowKey(m.entry)}
                        className="ranking-player-search-option"
                        // Keep focus in the box while clicking: the full-width search and its
                        // results only show while the box has focus (styles.css), so a press
                        // that blurred it would hide the row before the click lands.
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={() => jumpTo(m.entry.playerId)}
                      >
                        <span>{m.entry.shortName}</span>
                        <span className="rank-hint">#{m.index + 1}</span>
                      </div>
                    ))
                  ) : LIVE ? null : (
                    <div className="ranking-player-search-empty">No match in this list</div>
                  )}
                  {LIVE && query.trim().length >= MIN_SEARCH_LETTERS && (
                    <>
                      <div className="ranking-player-search-group">All players</div>
                      {elsewhere.map((h) => (
                        <div
                          key={h.id}
                          className="ranking-player-search-option"
                          onMouseDown={(ev) => ev.preventDefault()}
                          onClick={() => void jumpElsewhere(h)}
                        >
                          <span>{h.name}</span>
                          <span className="rank-hint">{h.team ?? ""}</span>
                        </div>
                      ))}
                      {searching && <div className="ranking-player-search-empty">Searching all players…</div>}
                      {!searching && elsewhere.length === 0 && matches.length === 0 && (
                        <div className="ranking-player-search-empty">No player found</div>
                      )}
                    </>
                  )}
                  {LIVE && query.trim().length < MIN_SEARCH_LETTERS && matches.length === 0 && (
                    <div className="ranking-player-search-empty">Type 3+ letters to search all players…</div>
                  )}
                </div>
              )}
              {findNote && <div className="ranking-player-search-empty">{findNote}</div>}
            </div>
            <FilterChipBar
              active={activeFilters}
              refPlayer={chips.refPlayer}
              options={chips.options}
              rangeFilters={chips.rangeFilters}
              onChange={onChipsChange}
              addMenuKeys={chips.addMenuKeys}
              newChipValue={chips.newChipValue}
              quietKeys={quietKeys}
              optionInfo={chips.optionInfo}
            />
          </div>

          {board.count === null ? null : entries.length === 0 ? (
            <div className="stats-empty">{emptyMessage}</div>
          ) : (
            <div className="ranking-list" ref={listRef}>
              {entries.map((e, i) => (
                <div
                  key={rowKey(e)}
                  className={`ranking-row${e.playerId === jumpedId ? " ranking-row-jumped" : ""}`}
                  onPointerDown={(ev) => onRowPointerDown(ev, e)}
                  data-tooltip={twice.get(positionRowKey(e))}
                >
                  <span className="ranking-rank">{i + 1}</span>
                  <span className="ranking-player">
                    <span className="ranking-player-name">{e.shortName}</span>
                    <span className="ranking-player-meta">
                      {/* The club alone (Ben, 2026-10-31: "Brentford will suffice"); the league only when there's no club. */}
                      {e.team || e.competition}
                    </span>
                  </span>
                  <span className="ranking-value">
                    {formatValueForMode(e.value, metric, useTotal ? "total" : "per90")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** A row's React key: her id, + the position on live position rows (she can be listed once per
 *  position — `live/ranking.ts`). */
function rowKey(e: ProtoRankingEntry): string {
  return `${e.playerId}:${e.competition ?? ""}`;
}
